/**
 * Model "class" for User - defines useful field metadata for building and validating forms
 * (only used for password reset and login)
 */

import { required, sameAs, email as emailValidator } from 'vuelidate/lib/validators'
import Api from '@/providers/Api'
import * as _ from 'lodash'
import Validation from '@/providers/Validation'
import Vue from 'vue'


const roleLabels = {
    ADMIN: 'Admin',
    AUDITOR: 'Auditor',
    EQUIP_OWNER: 'Contractor',
    EQUIP_OWNER_ADMIN: 'Contractor EO Admin',
    OWNERS_REP: 'Owners Rep',
    PROGRAM_ADMIN: 'Program Admin',
    PROJECT_MANAGER: 'Project Manager',
    SUPER_ADMIN: 'Super Admin',
}

//Custom vuelidate validators
async function one_phone_required (value) {        //  do NOT convert to (value)=> format
    let phone_office = this.$v.form.phone_office?.$model
    let phone_mobile = this.$v.form.phone_mobile?.$model
    return !!(phone_office || phone_mobile)

}

export default {
    getStatusLabel (value) {
        return value ? 'Disabled' : 'Enabled'
    },
    getRoleLabel (value) {
        return roleLabels[value]
    },
    getRoleLabels() {
        return roleLabels
    },
    async loadUserOptions (modelField, filters = {}) {
        const expands = []
        const users = await Api.getItems(null, 'user', filters, {}, expands)
        modelField.options = _.map(users, (user) => {
            return {
                value: user.id, text: user.full_name
            }
        })
    },

    fields: {
        id: {
            type: Number,
            showIf (model) {
                return false
            },
        },
        email: {
            label: 'Email',
            type: 'email',
            autocomplete: 'email',
            disableIf (model) {
                return !!model.activate_key  // don't allow change of email if activating account
            },
            validations: {
                required,
                email: v => v ? emailValidator(v.toLowerCase()): true,
                email_unique: async function (value) {        //  do NOT convert to (value)=> format
                    value = value.toLowerCase()
                    if (value) {
                        let filters = {
                            email: { field: 'email', type: 'string', value: value }
                        }
                        let user_id = this.$v.form['id'] ? this.$v.form['id'].$model : null
                        if (user_id) {
                            filters.id = { field: 'id', value: user_id, negate: true }
                        }
                        return !(await this.$api.emailExists(value)).body
                    }
                    return true
                }
            },
            invalidFeedback: {
                required: 'The email address is required',
                email_unique: 'This email address already exists',
            }
        },
        email_edit: { //Same as email for cases when we don't need to check uniqueness
            label: 'Email',
            type: 'email',
            dbFieldName: 'email',
            autocomplete: 'email',
            validations: {
                required,
                email: v => v ? emailValidator(v.toLowerCase()) : true,
            },
            invalidFeedback: {
                required: 'The email address is required',
            }
        },
        email_forgot: {
            label: 'Email',
            type: 'email',
            autocomplete: 'email',
            validations: {
                required,
            },
            invalidFeedback: {
                required: 'The forgotten email address is required',
            }
        },
        activate_key: {
            label: '',
            type: 'text',
            showIf (model) {
                return false // always hide
            }
        },
        first_name: {
            label: 'First Name',
            type: 'text',
            validations: { required },
        },
        last_name: {
            label: 'Last Name',
            type: 'text',
            validations: { required },
        },
        current_password: {
            label: 'Current Password',
            type: 'password',
            validations: { required },
            invalidFeedback: {
                required: 'Your current password is required to make any changes'
            },
        },
        password: {
            label: 'New Password',
            type: 'password',
            validations: { required, validatePassword: Validation.validatePassword },
            autocomplete: 'new-password',
            // invalidFeedback: {
            //     validatePassword: 'Password must be at least 4 characters and have at least one letter and one digit'
            // },
            getDescription () {
                return 'Password must be at least 4 characters and have at least one letter and one digit'
            },
        },
        password_repeat: {
            label: 'Repeat Password',
            type: 'password',
            validations: { sameAs: sameAs('password') },
            autocomplete: 'repeat-password',
            invalidFeedback: { sameAs: 'Passwords must match' }
        },
        phone_office: {
            label: 'Office Phone',
            type: 'text',
            onChange (model) {
                Vue.nextTick(() => {
                    model['phone_office'] = Validation.formatPhoneNumber(model['phone_office'])
                })
            },
            validations: {
                one_phone_required,
                phone: Validation.validatePhoneNumber
            },
            invalidFeedback: {
                one_phone_required: 'Either office phone or mobile phone required',
                phone: 'Must be a valid 9-digit phone number',
            },
        },
        phone_mobile: {
            label: 'Mobile Phone',
            type: 'text',
            onChange (model) {
                Vue.nextTick(() => {
                    model['phone_mobile'] = Validation.formatPhoneNumber(model['phone_mobile'])
                })
            },
            validations: {
                one_phone_required,
                phone: Validation.validatePhoneNumber
            },
            invalidFeedback: {
                one_phone_required: 'Either office phone or mobile phone required',
                phone: 'Must be a valid 9-digit phone number',
            },
            getDescription () {
                return 'Either office phone or mobile phone is required'
            },
        },
        disabled: {
            label: 'Disabled',
            type: 'check'
        },
        is_superadmin: {
            label: 'Super Admin',
            type: 'check'
        },
        is_admin: {
            label: 'Application Admin',
            type: 'check'
        },
        is_auditor: {
            label: 'Auditor',
            type: 'check'
        },
        //Not part of User table but used in sign up for some roles
        department: {
            label: 'Bureau / Dept.',
            type: 'text'
        },
        company_name: {
            label: 'Company',
            type: 'text'
        },
        agency_names: {
            label: 'Agency',
            type: 'text'
        },
        company_names: {
            label: 'Company',
            type: 'text'
        },
        //  AgencyUser
        is_program_admin: {
            label: 'Is Program Admin',
            type: 'check'
        },
        is_project_manager: {
            label: 'Is Project Manager',
            type: 'check'
        },
        is_agency_admin: {
            label: 'Is Agency Admin',
            type: 'check'
        },
        twofa_enabled: {
            label: 'Two-Factor Authentication using Google Authenticator',
            type: 'check'
        },
    }
}
